import "./outlet.css";
import "../user/user.css";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUserStart, getUserSuccess, getUserFailure } from "../../redux/userRedux";
import { publicRequest, userRequest } from "../../requestMethods";
import moment from "moment";
import styled from "styled-components";
import { FiEdit2, FiLock, FiPlus, FiX } from "react-icons/fi";

const ModalContainer = styled.div`
    height: 100%;
    width: 100%;
    opacity: 0.75;
    background: black;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    position: fixed;
    z-index: 100;
`;

const OverlayContainer = styled.div`
    overflow: hidden;

    @media (max-width: 768px) {
        overflow-y: auto;
        max-height: 100vh;
        -webkit-overflow-scrolling: touch;
    }
`;

const Overlay = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100%;
    color: white;
    background-color: transparent;
    visibility: visible;
    z-index: 101;

    @media (min-width: 769px) {
        padding: 48px 24px;
        box-sizing: border-box;
        overflow-y: auto;
    }

    @media (max-width: 768px) {
        padding-top: 80px;
        align-items: flex-start;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
    }
`;

const ModalDetails = styled.div`
    background-color: transparent;
    color: black;
    width: 55%;
    max-width: 820px;
    display: flex;
    justify-content: center;
    z-index: 102;

    @media (min-width: 769px) {
        height: auto;
        max-height: calc(100vh - 96px);
    }

    @media (max-width: 768px) {
        width: 90%;
        height: auto;
        max-height: 90vh;
        align-items: flex-start;
    }
`;

const ModalItemContainer = styled.div`
    background-color: white;
    color: black;
    width: 100%;
    display: grid;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
    box-sizing: border-box;
    max-height: calc(100vh - 200px);
    overflow-y: auto;

    @media (min-width: 769px) {
        max-height: calc(100vh - 96px);
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        padding: 32px;
        gap: 12px;

        & .addProductForm {
            width: 100%;
            max-width: 760px;
            margin: 0 auto;
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 16px 24px;
        }

        & .addProductItem {
            width: 100%;
        }

        & .addProductItem:last-of-type {
            grid-column: 1 / -1;
        }

        & .updateProductButton {
            grid-column: 1 / -1;
            justify-self: center;
            width: 240px;
        }
    }

    @media (max-width: 768px) {
        height: auto;
        min-height: fit-content;
        max-height: 90vh;
        overflow-y: auto;
        padding: 12px;
        display: flex;
        flex-direction: column;

        & .addProductForm {
            width: 100%;
            display: flex;
            flex-direction: column;
        }

        & .addProductItem {
            width: 100%;
        }

        & .addProductItem input,
        & .addProductItem select,
        & .addProductItem textarea {
            width: 100%;
            box-sizing: border-box;
        }

        & .updateProductButton {
            margin-top: 20px;
            margin-bottom: 12px;
        }
    }
`;

const DesktopCloseButton = styled.button`
    display: none;
    border: none;
    background: transparent;
    color: #111827;
    padding: 8px;
    font-size: 22px;
    cursor: pointer;
    align-self: flex-start;

    @media (min-width: 769px) {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 4px;
    }
`;

const MobileCloseButton = styled.button`
    display: none;
    border: none;
    background: transparent;
    color: #111827;
    padding: 6px 8px;
    font-size: 22px;
    cursor: pointer;
    align-self: flex-start;

    @media (max-width: 768px) {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 8px;
    }
`;

export default function Outlet() {
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.user.currentUser);
    const products = useSelector((state) => state.user.products);
    const [isLoaded, setIsLoaded] = useState(false);
    const [user, setUser] = useState(undefined);
    const [editModalState, setEditModalState] = useState(false);
    const [statusUser, setStatusUser] = useState(undefined);

    const canEditStaff =
        currentUser?.isSuperAdmin === true ||
        currentUser?.type === "superadmin" ||
        currentUser?.type === "manager" ||
        currentUser?.type === "outright";

    const fetchAdmins = useCallback(async () => {
        dispatch(getUserStart());
        try {
            let principleParam;

            if (currentUser?.isSuperAdmin) {
                principleParam = undefined;
            } else if (currentUser?.type === "outright") {
                principleParam = currentUser._id;
            } else if (currentUser?.type === "manager") {
                principleParam = currentUser?.principle;
            } else {
                principleParam = currentUser?.principle;
            }

            const params = {
                isAdmin: true,
            };
            if (principleParam !== undefined) {
                params.principle = principleParam;
            }

            const res = await publicRequest.get("/users/admin", {
                params,
            });

            dispatch(getUserSuccess(res.data));
            setIsLoaded(true);
        } catch (err) {
            dispatch(getUserFailure());
            console.error("Error fetching outlets:", err);
        }
    }, [dispatch, currentUser]);

    useEffect(() => {
        if (currentUser) {
            fetchAdmins();
        }
    }, [currentUser, fetchAdmins]);

    const handleChange = (e) => {
        setUser((prev) => {
            return { ...prev, [e.target.name]: e.target.value };
        });
    };

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setUser((prev) => ({
            ...prev,
            address: { ...(prev?.address || {}), [name]: value },
        }));
    };

    const addressFields = [
        { name: "lineOne", label: "Address Line 1" },
        { name: "lineTwo", label: "Address Line 2" },
        { name: "zip", label: "Postcode" },
        { name: "city", label: "City / State" },
        { name: "country", label: "Country" },
    ];

    const handleStatusSave = async () => {
        if (!canEditStaff || !statusUser?._id) return;

        try {
            const result = await userRequest.put(`/users/adminUpdate/${statusUser._id}`, {
                status: statusUser.status || "active",
            });
            if (result.data) {
                alert("Status has been updated successfully");
                setStatusUser(undefined);
                await fetchAdmins();
            } else {
                alert("Failed to update status");
            }
        } catch (err) {
            alert("Failed to update status");
        }
    };

    const handleClick = async () => {
        if (!canEditStaff) return;

        const userToUpdate = { ...user };

        if (!userToUpdate.password || userToUpdate.password.trim() === "") {
            delete userToUpdate.password;
        }

        try {
            const result = await userRequest.put(
                `/users/adminUpdate/${user._id}`,
                userToUpdate
            );
            if (result.data) {
                alert("User has been updated successfully");
                setEditModalState(false);
                setUser(undefined);
                await fetchAdmins();
            }
        } catch (err) {
            alert("User failed to update");
        }
    };

    const columns = [
        {
            Header: "Prefix",
            accessor: "debtorCode",
        },
        {
            Header: "Name",
            accessor: "name",
        },
        {
            Header: "Phone Number",
            accessor: "phoneNumber",
        },
        {
            Header: "Email",
            accessor: "email",
        },
        {
            Header: "Type",
            accessor: "type",
        },
        {
            Header: "Status",
            accessor: "status",
        },
        {
            Header: "Member Since",
            accessor: "createdAt",
        },
        ...(canEditStaff
            ? [
                  {
                      Header: "Action",
                      accessor: "action",
                  },
              ]
            : []),
    ];

    return (
        <div className="container mx-auto px-4 py-8 block">
            <div className="flex justify-between items-center mb-6">
                <button
                    onClick={() => fetchAdmins()}
                    className="flex items-center bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
                >
                    <FiPlus className="mr-2" />
                    Refresh
                </button>
            </div>

            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-orange-400">
                        <tr>
                            {columns.map((column) => (
                                <th
                                    key={column.Header}
                                    className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider"
                                >
                                    {column.Header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {isLoaded && products ? products.map((outlet) => (
                            <tr key={outlet._id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {outlet.debtorCode ? outlet.debtorCode : ""}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {outlet.name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {outlet.phoneNumber}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {outlet.email ? outlet.email : ""}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                                    {outlet.type}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                                    {outlet.status}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {moment(outlet.createdAt).format("DD/MM/YYYY")}
                                </td>
                                {canEditStaff && (
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                title="Edit"
                                                aria-label={`Edit ${outlet.name}`}
                                                onClick={() => {
                                                    const { password, ...restUser } = outlet;
                                                    setUser(restUser);
                                                    window.scrollTo(0, 0);
                                                    setEditModalState(true);
                                                }}
                                                className="inline-flex items-center justify-center w-7 h-7 rounded text-gray-800 hover:text-black"
                                            >
                                                <FiEdit2 size={16} />
                                            </button>
                                            <button
                                                type="button"
                                                title="Change status"
                                                aria-label={`Change status for ${outlet.name}`}
                                                onClick={() =>
                                                    setStatusUser({
                                                        _id: outlet._id,
                                                        name: outlet.name,
                                                        status: outlet.status || "active",
                                                    })
                                                }
                                                className="inline-flex items-center justify-center w-7 h-7 rounded text-gray-800 hover:text-black"
                                            >
                                                <FiLock size={16} />
                                            </button>
                                        </div>
                                    </td>
                                )}
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={columns.length} className="px-6 py-4 text-center text-gray-500">
                                    Loading...
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {canEditStaff && statusUser && (
                <OverlayContainer>
                    <ModalContainer onClick={() => setStatusUser(undefined)} />
                    <Overlay onClick={() => setStatusUser(undefined)}>
                        <div
                            className="bg-white text-gray-900 rounded-lg shadow-lg p-6 w-full max-w-sm"
                            style={{ position: "relative", zIndex: 102 }}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <button
                                type="button"
                                aria-label="Close status form"
                                onClick={() => setStatusUser(undefined)}
                                style={{
                                    position: "absolute",
                                    top: 12,
                                    right: 12,
                                    zIndex: 2,
                                    border: "none",
                                    background: "transparent",
                                    color: "#111827",
                                    cursor: "pointer",
                                    padding: 4,
                                    lineHeight: 0,
                                }}
                            >
                                <FiX size={20} />
                            </button>
                            <h3 className="text-lg font-semibold mb-1">Change Status</h3>
                            <p className="text-sm text-gray-600 mb-4">{statusUser.name}</p>
                            <label className="block text-sm font-medium mb-2">Status</label>
                            <select
                                value={statusUser.status || "active"}
                                onChange={(event) =>
                                    setStatusUser((prev) => ({
                                        ...prev,
                                        status: event.target.value,
                                    }))
                                }
                                className="w-full border border-gray-300 rounded px-3 py-2 mb-5"
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                            <button
                                type="button"
                                onClick={handleStatusSave}
                                className="w-full bg-black text-white rounded py-2 hover:bg-gray-800"
                            >
                                Update Status
                            </button>
                        </div>
                    </Overlay>
                </OverlayContainer>
            )}

            {canEditStaff && editModalState && user && (
                <OverlayContainer>
                    <ModalContainer
                        onClick={() => setEditModalState(false)}
                    ></ModalContainer>
                    <Overlay onClick={() => setEditModalState(false)}>
                        <ModalDetails onClick={(event) => event.stopPropagation()}>
                            <ModalItemContainer>
                                <DesktopCloseButton
                                    aria-label="Close edit user form"
                                    onClick={() => setEditModalState(false)}
                                >
                                    <FiX />
                                </DesktopCloseButton>
                                <MobileCloseButton
                                    aria-label="Close edit user form"
                                    onClick={() => setEditModalState(false)}
                                >
                                    <FiX />
                                </MobileCloseButton>
                                <div className="updateTitle">
                                    <form className="addProductForm">
                                        <div className="addProductItem">
                                            <label>Full Name</label>
                                            <input
                                                name="name"
                                                type="text"
                                                placeholder={user.name}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="addProductItem">
                                            <label>Email</label>
                                            <input
                                                name="email"
                                                type="text"
                                                placeholder={user.email}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="addProductItem">
                                            <label>Phone Number</label>
                                            <input
                                                name="phoneNumber"
                                                type="text"
                                                placeholder={user.phoneNumber}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="addProductItem">
                                            <label>IC</label>
                                            <input
                                                name="ic"
                                                type="text"
                                                placeholder={user.ic || "IC Number"}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        {user.type === "outright" &&
                                            addressFields.map(({ name, label }) => (
                                                <div className="addProductItem" key={name}>
                                                    <label>{label}</label>
                                                    <input
                                                        name={name}
                                                        type="text"
                                                        placeholder={user.address?.[name] || label}
                                                        onChange={handleAddressChange}
                                                    />
                                                </div>
                                            ))}
                                        {user.type === "outright" && (
                                            <div className="addProductItem">
                                                <label>Business Registration Number</label>
                                                <input
                                                    name="businessRegNo"
                                                    type="text"
                                                    placeholder={user.businessRegNo || "Business Registration Number"}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        )}
                                        <div className="addProductItem">
                                            <label>{user.type === "outright" ? "Business TIN" : "TIN"}</label>
                                            <input
                                                name="tin"
                                                type="text"
                                                placeholder={user.tin || "Tax Identification Number"}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        {user.type === "outright" && (
                                            <>
                                                <div className="addProductItem">
                                                    <label>Business Sales Tax Number</label>
                                                    <input
                                                        name="salesTaxNo"
                                                        type="text"
                                                        placeholder={user.salesTaxNo || "Business Sales Tax Number"}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                                <div className="addProductItem">
                                                    <label>Bank Account Number</label>
                                                    <input
                                                        name="bankAccount"
                                                        type="text"
                                                        placeholder={user.bankAccount || "Bank Account Number"}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                            </>
                                        )}
                                        <div className="addProductItem">
                                            <label>Password</label>
                                            <input
                                                name="password"
                                                type="password"
                                                placeholder="******"
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="addProductItem">
                                            <label>Remarks</label>
                                            <textarea
                                                name="remark"
                                                type="text"
                                                placeholder={user.remark || "Enter remarks"}
                                                onChange={handleChange}
                                                rows="4"
                                            />
                                        </div>
                                        <div
                                            onClick={handleClick}
                                            className="updateProductButton"
                                        >
                                            Update User
                                        </div>
                                    </form>
                                </div>
                            </ModalItemContainer>
                        </ModalDetails>
                    </Overlay>
                </OverlayContainer>
            )}
        </div>
    );
}
